import { CommonHelperFunctions, IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

// KOA-5857 — Inline ManagedDB row editor (rendered in the data-diagram canvas).
// Two endpoints: a paged read and a mixed-op batch write.
// Writes against K-prefixed system tables are rejected server-side via
// EnsureWritableUserTable in DatasourceService.TableEditor.cs.

export type ManagedDbFilterOperator =
  | "eq"
  | "neq"
  | "contains"
  | "notContains"
  | "startsWith"
  | "endsWith"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "isNull"
  | "isNotNull"
  | "in"
  | "notIn";
export type ManagedDbExportFormat = "csv" | "xlsx" | "insert" | "update" | "upsert";

export interface IManagedDbColumnFilter {
  column: string;
  operator: ManagedDbFilterOperator;
  value?: unknown;
}

export interface IGetManagedDbRowsRequest {
  tableSchema?: string;
  pageNumber: number;
  pageSize: number;
  sortColumn?: string;
  sortDir?: "asc" | "desc";
  filters: IManagedDbColumnFilter[];
}

export interface IExportManagedDbRowsRequest {
  tableSchema?: string;
  format: ManagedDbExportFormat;
  sortColumn?: string;
  sortDir?: "asc" | "desc";
  filters: IManagedDbColumnFilter[];
}

// Column names are user-defined and value types are untyped server-side
// (Dictionary<string, object>). Modelled as a named index-signature
// interface — `Record<string, unknown>` is banned by CLAUDE.md.
export interface IManagedDbRow {
  [columnName: string]: unknown;
}

// Backend's DatabaseFkModel includes RefTableSchema; the shared
// IDatabaseFkModel in kuika-cl-model omits it, so define the richer shape
// locally rather than extending the shared type (no other consumer needs the
// schema field today). refColumnName is also row-editor-only — populated
// server-side by looking up the referenced table's primary-key column so
// "Go to referenced row" can navigate without assuming the column is "Id".
export interface IManagedDbForeignKey {
  name: string;
  tableName: string;
  tableSchema?: string;
  columnName: string;
  refTableName: string;
  refTableSchema?: string;
  refColumnName?: string;
}

// Inbound (reverse) FK — an FK on FromTable.FromColumn that references
// the CURRENT table's RefColumn. Powers the row context menu's
// "Referenced by" submenu.
export interface IManagedDbInboundForeignKey {
  name: string;
  fromTableName: string;
  fromTableSchema?: string;
  fromColumnName: string;
  refColumnName: string;
}

export interface IManagedDbRowsResponse {
  columns: IDatabaseColumnModel[];
  rows: IManagedDbRow[];
  totalCount: number;
  primaryKeyColumns: string[];
  // True for tables on the server's exact Kuika system-table list
  // (ManagedDbConstants.KuikaTables). Drives the editor's read-only
  // banner and the import / export / AI affordance gating. Authoritative
  // — do NOT approximate this with a name prefix check.
  isSystemTable: boolean;
  foreignKeys: IManagedDbForeignKey[];
  inboundForeignKeys: IManagedDbInboundForeignKey[];
  // Names of columns the server stripped from `columns` / row payloads
  // because they matched the sensitive-column policy (password / token
  // / apikey / encrypted* / dek / iv / authtag, or an explicit entry in
  // SensitiveColumnsByTable). Surfaced so the table editor can show a
  // visible "N sensitive columns hidden" affordance — silent
  // disappearance was the original UX problem. Always present; empty
  // when nothing was hidden.
  hiddenColumns?: string[];
}

export interface IManagedDbRowKeyEntry {
  column: string;
  value: unknown;
}

export interface IManagedDbRowKey {
  keys: IManagedDbRowKeyEntry[];
}

export interface IManagedDbRowMutation {
  clientId: string;
  op: "insert" | "update" | "delete";
  key?: IManagedDbRowKey;
  values?: IManagedDbRow;
  // Optimistic-concurrency snapshot: the values the client loaded for each
  // column it's now changing. Server appends these to the UPDATE WHERE so
  // a concurrent edit by another writer is detected before the SQL commits.
  // Only meaningful on op === "update"; ignored otherwise.
  originalValues?: IManagedDbRow;
}

export interface IManagedDbBatchRequest {
  tableName: string;
  tableSchema?: string;
  // When true the server wraps every mutation in one transaction — any
  // failure rolls all of them back. Default false (best-effort per row).
  atomic?: boolean;
  mutations: IManagedDbRowMutation[];
}

export interface IManagedDbBatchResult {
  clientId?: string;
  success: boolean;
  error?: string;
  // True when the failure was an optimistic-concurrency conflict (the row
  // exists but another writer changed it since the client loaded it). The
  // save bar surfaces a distinct "Reload and retry" affordance for these.
  conflict?: boolean;
}

export interface IManagedDbBatchResponse {
  success: boolean;
  // Server echoes whether the batch actually ran as a single transaction.
  // Always false when the request didn't ask for atomic.
  atomic?: boolean;
  results: IManagedDbBatchResult[];
}

// Pre-save SQL preview — one item per mutation, in submission order.
// Mirrors what the Save would execute via the same builder code path
// so the displayed text can't drift from the executed text.
export interface IManagedDbBatchPreviewParameter {
  name: string;
  // Server-formatted display string (culture-invariant). Render as-is —
  // not parsed back into a value, the preview is read-only.
  value: string;
}

export interface IManagedDbBatchPreviewItem {
  clientId?: string;
  op: string;
  sql?: string;
  parameters: IManagedDbBatchPreviewParameter[];
  // Populated for mutations that would have failed validation before
  // reaching SQL (unknown column, missing PK on update, etc.). The
  // preview surfaces these so the user sees the problem before clicking
  // Save and chasing a per-row error in the response.
  error?: string;
}

export interface IManagedDbBatchPreviewResponse {
  items: IManagedDbBatchPreviewItem[];
}

export interface IAiFillRowRequest {
  tableSchema?: string;
  currentRow: IManagedDbRow;
  hint?: string;
}

export interface IAiFillRowResponse {
  values: IManagedDbRow;
  warnings: string[];
}

export interface IAiGenerateRowsRequest {
  tableSchema?: string;
  count: number;
  hint?: string;
}

export interface IAiGenerateRowsResponse {
  rows: IManagedDbRow[];
  warnings: string[];
}

export interface IImportRowError {
  rowIndex: number;
  error: string;
}

export interface IImportRowsResponse {
  inserted: number;
  failed: number;
  warnings: string[];
  errors: IImportRowError[];
}

const backendUrl: string = CommonHelperFunctions.GetBackendUrl();

export class TableEditorService {
  public static QueryRows(
    tableName: string,
    request: IGetManagedDbRowsRequest,
    signal?: AbortSignal
  ): Promise<AxiosResponse<IManagedDbRowsResponse>> {
    try {
      const encoded: string = encodeURIComponent(tableName);
      return Axios.post<IManagedDbRowsResponse>(
        `${backendUrl}/datasource/manageddb/tables/${encoded}/rows/query`,
        request,
        { signal }
      );
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  public static BatchMutate(request: IManagedDbBatchRequest): Promise<AxiosResponse<IManagedDbBatchResponse>> {
    try {
      const encoded: string = encodeURIComponent(request.tableName);
      return Axios.post<IManagedDbBatchResponse>(
        `${backendUrl}/datasource/manageddb/tables/${encoded}/rows/batch`,
        request
      );
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  // Pre-save preview — same payload shape as BatchMutate, no side effects.
  // The signal lets the caller cancel a slow preview if the user discards
  // their pending changes or closes the modal before it returns.
  public static BatchPreview(
    request: IManagedDbBatchRequest,
    signal?: AbortSignal
  ): Promise<AxiosResponse<IManagedDbBatchPreviewResponse>> {
    try {
      const encoded: string = encodeURIComponent(request.tableName);
      return Axios.post<IManagedDbBatchPreviewResponse>(
        `${backendUrl}/datasource/manageddb/tables/${encoded}/rows/batch/preview`,
        request,
        { signal }
      );
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  public static AiFillRow(
    tableName: string,
    request: IAiFillRowRequest,
    signal?: AbortSignal
  ): Promise<AxiosResponse<IAiFillRowResponse>> {
    try {
      const encoded: string = encodeURIComponent(tableName);
      return Axios.post<IAiFillRowResponse>(
        `${backendUrl}/datasource/manageddb/tables/${encoded}/ai/fill-row`,
        request,
        { signal }
      );
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  public static AiGenerateRows(
    tableName: string,
    request: IAiGenerateRowsRequest,
    signal?: AbortSignal
  ): Promise<AxiosResponse<IAiGenerateRowsResponse>> {
    try {
      const encoded: string = encodeURIComponent(tableName);
      return Axios.post<IAiGenerateRowsResponse>(
        `${backendUrl}/datasource/manageddb/tables/${encoded}/ai/generate-rows`,
        request,
        { signal }
      );
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  // Builds the export URL the streaming helper will fetch directly via
  // the File System Access API path — extracted so the streaming code
  // (which bypasses Axios to keep the response body as a ReadableStream)
  // shares the exact same parameter-encoding contract as the buffered
  // Axios path below.
  public static BuildExportUrl(tableName: string, request: IExportManagedDbRowsRequest): string {
    const encoded: string = encodeURIComponent(tableName);
    const params = new URLSearchParams();
    params.set("format", request.format);
    if (request.tableSchema) {
      params.set("tableSchema", request.tableSchema);
    }
    if (request.sortColumn) {
      params.set("sortColumn", request.sortColumn);
      if (request.sortDir) {
        params.set("sortDir", request.sortDir);
      }
    }
    if (request.filters.length > 0) {
      params.set("filters", JSON.stringify(request.filters));
    }
    return `${backendUrl}/datasource/manageddb/tables/${encoded}/export?${params.toString()}`;
  }

  public static ExportRows(
    tableName: string,
    request: IExportManagedDbRowsRequest,
    signal?: AbortSignal
  ): Promise<AxiosResponse<Blob>> {
    try {
      return Axios.get<Blob>(TableEditorService.BuildExportUrl(tableName, request), {
        responseType: "blob",
        signal
      });
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }

  // The signal lets the caller abort an in-flight upload (modal close,
  // table switch). Without it, a long-running multipart POST keeps running
  // after the user has moved on, and the response handler fires setState
  // on an unmounted component.
  // mode: "insert" (default) — every parsed row becomes an INSERT.
  // "upsert" keys on the table's primary key: existing rows are updated
  // in place, new rows are inserted. Upsert requires the file to carry
  // the PK column(s); the server validates and bails with a clear
  // error if the table has no PK.
  // dryRun: when true the server parses + validates + builds each row's
  // command but skips the actual write. The response shape is identical
  // (Inserted / Failed / Warnings / Errors) so the modal renders the
  // same summary; the leading warning identifies it as a dry run.
  public static ImportRows(
    tableName: string,
    file: File,
    format: "csv" | "xlsx",
    tableSchema?: string,
    mode?: "insert" | "upsert",
    dryRun?: boolean,
    signal?: AbortSignal
  ): Promise<AxiosResponse<IImportRowsResponse>> {
    try {
      const encoded: string = encodeURIComponent(tableName);
      const params = new URLSearchParams();
      params.set("format", format);
      if (tableSchema) {
        params.set("tableSchema", tableSchema);
      }
      if (mode && mode !== "insert") {
        params.set("mode", mode);
      }
      if (dryRun) {
        params.set("dryRun", "true");
      }
      const form = new FormData();
      form.append("file", file);
      return Axios.post<IImportRowsResponse>(
        `${backendUrl}/datasource/manageddb/tables/${encoded}/import?${params.toString()}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, signal }
      );
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
      throw error;
    }
  }
}
